import React, { useState, useEffect } from "react";
import styled from "styled-components";

import { settings } from "../../data/settings";
import theme from "../../theme";
import { useNavigate, useLocation } from "react-router-dom";
import { FaLinkedinIn } from "react-icons/fa";

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  text-decoration: none;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--my-color5);
    color: #fff;
    box-shadow: 0 2px 8px #0002;
    transition:
      background 0.3s ease,
      transform 0.3s ease;
  }

  &:hover span {
    background: var(--my-color4);
    transform: scale(1.05);
  }
`;

const FooterWrapper = styled.footer`
  background-color: #000000;
  color: #fff;
  padding: 3rem 2rem 1rem 2rem;
  font-weight: 100;
  font-size: 0.875rem; !important;
  ${theme.breakpoints.down("sm")} {
    padding: 2rem 1rem 1rem 1rem;
    font-size: 0.8rem; !important;
  } 
`;

const Columns = styled.div`
  max-width: ${({ maxWidth }) => maxWidth || "100%"};
  display: flex;
  flex-wrap: wrap;
  gap: 5rem;
  justify-content: space-between;
  margin: auto;

  ${theme.breakpoints.down("sm")} {
    flex-direction: column;
    gap: 1rem;
    padding: 0rem 1rem;
    margin: auto;
    margin-top: 2rem;
  }
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const ColumnTop = styled.div``;

const Logo = styled.div`
  max-width: 7rem;
  margin-bottom: 0rem;
  background: white;
  padding: 0.5rem;
  border-radius: 2rem;
  border-bottom-left-radius: 2rem;
  padding-right: 1rem;
  background: #ffffff;
  margin-bottom: 0.5rem;
  & img {
    width: 100%;
    margin-bottom: -5px;
    margin-top: -1px;
  }
`;

const FooterBottom = styled.div`
  margin-top: 4rem;
  text-align: center;
  color: #aaa;
`;

const FooterTitle = styled.div`
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  margin-top: 2.5rem;
`;

const FooterText = styled.div`
  margin-bottom: ${({ marginBottom }) => marginBottom || "0.5rem"};
  ${({ isBold }) => isBold && "font-weight: bold;"}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
  color: ${({ color }) => color || "#fff"};
  text-transform: ${({ textTransform }) => textTransform || "none"};
  text-decoration: none;
  & a {
    text-decoration: none;
    color: ${({ color }) => color || "#fff"};
  }
`;

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToId = (id) => {
    const el = document.getElementById(id);

    const getNavbarHeightInPixels = () => {
      const heightStr = settings.navbar?.height || "5rem";
      if (heightStr.endsWith("rem")) {
        const remValue = parseFloat(heightStr);
        const baseFontSize = parseFloat(
          getComputedStyle(document.documentElement).fontSize,
        );
        return remValue * baseFontSize;
      } else if (heightStr.endsWith("px")) {
        return parseFloat(heightStr);
      } else {
        return parseFloat(heightStr); // fallback
      }
    };

    if (el) {
      const navbarHeight = getNavbarHeightInPixels();
      const yOffset = -navbarHeight;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      window.history.pushState(null, "", `#${id}`);
    }
  };

  const handleClick = (id) => {
    if (location.pathname !== "/") {
      // Stocker temporairement l’ancre dans le state de navigation
      navigate(`/#${id}`);
    } else {
      scrollToId(id);
    }
  };

  // Gérer le scroll automatique si on vient d’une autre page avec un hash
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // Laisse un peu de temps au DOM pour rendre les éléments
      setTimeout(() => {
        scrollToId(id);
      }, 300);
    }
  }, [location]);

  return (
    <FooterWrapper>
      <Columns maxWidth={settings?.pages?.pageMaxWidth}>
        {/* Présentation */}
        <Column style={{ justifyContent: "space-between" }}>
          <ColumnTop>
            <Logo>{settings?.navbar?.logoImg}</Logo>
            <FooterText
              marginBottom="0.5rem"
              dangerouslySetInnerHTML={{
                __html: settings?.footer?.description || "",
              }}
            />
            <br />
            <SocialLink
              href="https://www.linkedin.com/in/vincent-deliencourt/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>
                <FaLinkedinIn size={20} />
              </span>
            </SocialLink>
          </ColumnTop>
        </Column>

        {/* Contact */}
        <Column>
          <FooterTitle>Contact</FooterTitle>
          <FooterText
            dangerouslySetInnerHTML={{
              __html: settings?.footer?.address || "",
            }}
          />
          <FooterText
            color="white"
            textDecoration="none"
            sx={{
              margin: "0.5rem 0rem",
              textDecoration: "none",
              color: "white",
              "& a": {
                textDecoration: "none",
                color: "white",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: settings?.footer?.phone || "",
            }}
          />
        </Column>

        {/* Navigation */}
        <Column style={{ flex: "unset !important" }}>
          <FooterTitle>Navigation</FooterTitle>
          {Object.keys(settings?.pages).map((pageKey) => (
            <FooterText
              key={pageKey}
              variant="body2"
              marginBottom="0rem"
              textTransform="uppercase"
            >
              <div
                onClick={() => handleClick(pageKey)}
                style={{
                  color: "inherit",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                {settings?.pages[pageKey]?.title}
              </div>
            </FooterText>
          ))}
        </Column>
      </Columns>

      {/* Bas de page */}
      <FooterBottom>
        <FooterText>
          Ⓒ {new Date().getFullYear()} {settings?.compagnieName} - Tous droits
          réservés
          <br />
          <a
            href="/mentions-legales"
            style={{ color: "white", textDecoration: "none" }}
          >
            Mentions légales
          </a>
          <br />
          Réalisé avec ❤️ par{" "}
          <a
            href="https://digibuddy.fr/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "white", textDecoration: "none" }}
          >
            DigiBuddy
          </a>
        </FooterText>
      </FooterBottom>
    </FooterWrapper>
  );
};

export default Footer;
